import { createContext, type PropsWithChildren, use, useEffect, useMemo, useRef } from 'react';
import { createStore, type StoreApi, useStore } from 'zustand';
import { useShallow } from 'zustand/shallow';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { noop } from '@interxfi/utilities/fn/no-op';
import type { BaseFormWizardStepGeneric } from './step/form-wizard-step';
import type { FormWizardStepBuilderReturnType } from './step/form-wizard-step-builder';

export type FormWizardDataContext<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined> = {
  initialContext: TContext | undefined;
  context: TContext | undefined;
  setContext: (context: TContext | undefined) => void;
  dirtyStepRecord: Partial<Record<keyof TSteps, boolean>>;
  markStepDirty<K extends keyof TSteps>(key: K): void;
  steps: TSteps;
  initialData: TSteps;
  get<K extends keyof TSteps>(key: K): TSteps[K];
  set<K extends keyof TSteps>(key: K, value: TSteps[K]): TSteps;
  reset: (data?: TSteps, context?: TContext) => void;
  resetSteps: (data?: TSteps) => void;
  clear(): void;
};

const FormWizardContext = createContext<{
  manager: FormWizardStepBuilderReturnType<any, any>;
  store: StoreApi<FormWizardDataContext<any, any>>;
}>({
  store: null,
  manager: {
    addStep: noop,
    updateStep: noop,
    steps: STABLE_EMPTY_ARRAY_REF,
  },
} as any);

export type FormWizardDataSelector<
  TSteps extends BaseFormWizardStepGeneric,
  TContext extends object | undefined,
  T = FormWizardDataContext<TSteps, TContext>,
> = (state: FormWizardDataContext<TSteps, TContext>) => T;

function defaultSelector<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined>(
  state: FormWizardDataContext<TSteps, TContext>,
): FormWizardDataContext<TSteps, TContext> {
  return state as FormWizardDataContext<TSteps, TContext>;
}

export function FormWizardContextProvider<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined = undefined>({
  children,
  initialContext,
  initialData,
  manager,
}: PropsWithChildren<{ initialContext: TContext | undefined; initialData: TSteps; manager: FormWizardStepBuilderReturnType<TSteps, TContext> }>) {
  const storeRef = useRef<StoreApi<FormWizardDataContext<TSteps, TContext>>>(null);
  if (!storeRef.current) {
    storeRef.current = createStore<FormWizardDataContext<TSteps, TContext>>((set, get) => ({
      initialContext,
      context: initialContext,
      setContext: (context) => set({ context }),
      steps: initialData,
      initialData,
      clear: () => set({ steps: initialData, dirtyStepRecord: {} }),
      get: (key) => get().steps[key],
      set: (key, value) => {
        const newSteps = { ...get().steps, [key]: value };
        set({ steps: newSteps });
        return newSteps;
      },
      resetSteps: (data) => {
        const { initialData } = get();
        set({ steps: data ?? initialData, dirtyStepRecord: {} });
      },
      reset: (data, context) => {
        const { initialContext, initialData } = get();
        set({ steps: data ?? initialData, context: context ?? initialContext, dirtyStepRecord: {} });
      },

      dirtyStepRecord: {},
      markStepDirty: (key) => set((state) => ({ dirtyStepRecord: { ...state.dirtyStepRecord, [key]: true } })),
    }));
  }

  useEffect(() => {
    if (storeRef.current) {
      storeRef.current.setState({ steps: initialData });
    }
  }, [initialData]);

  const value = useMemo(
    () => ({
      manager,
      store: storeRef.current!,
    }),
    [manager],
  );

  return <FormWizardContext.Provider value={value}>{children}</FormWizardContext.Provider>;
}

export function useFormWizardContext<
  TSteps extends BaseFormWizardStepGeneric,
  TContext extends object | undefined,
  T = FormWizardDataContext<TSteps, TContext>,
>(selector: FormWizardDataSelector<TSteps, TContext, T> = defaultSelector as FormWizardDataSelector<TSteps, TContext, T>): T {
  const context = use(FormWizardContext);
  if (!context) {
    throw new Error('useFormWizardContext must be used within a FormWizardContextProvider');
  }

  return useStore(context.store, selector);
}

export function useFormWizardReset<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined = undefined>() {
  return useFormWizardContext<TSteps, TContext, (data?: TSteps, context?: TContext) => void>(useShallow((state) => state.reset));
}

export function useFormWizardResetSteps<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined = undefined>() {
  return useFormWizardContext<TSteps, TContext, (data?: TSteps) => void>(useShallow((state) => state.resetSteps));
}

export function useFormWizardInitialData<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined = undefined>() {
  return useFormWizardContext<TSteps, TContext, TSteps>(useShallow((state) => state.initialData));
}

export function useSetFormWizardContext<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined = undefined>() {
  return useFormWizardContext<TSteps, TContext, (newCtx: TContext | undefined) => void>(useShallow((state) => state.setContext));
}

export function useFormWizardContextValue<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined = undefined>() {
  return useFormWizardContext<TSteps, TContext, TContext | undefined>(useShallow((state) => state.context));
}

export function useFormWizardSetStepValue<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined = undefined>() {
  return useFormWizardContext<TSteps, TContext, <K extends keyof TSteps>(key: K, value: TSteps[K]) => TSteps>(useShallow((state) => state.set));
}

export function useFormWizardGetStepValue<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined = undefined>() {
  return useFormWizardContext<TSteps, TContext, <K extends keyof TSteps>(key: K) => TSteps[K]>(useShallow((state) => state.get));
}

export function useFormWizardSteps<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined = undefined>() {
  return useFormWizardContext<TSteps, TContext, TSteps>(useShallow((state) => state.steps));
}

export function useFormWizardStepValueSelector<
  TSteps extends BaseFormWizardStepGeneric,
  K extends keyof TSteps,
  R,
  TContext extends object | undefined = undefined,
>(key: K, selector?: (value: TSteps[K]) => R): R {
  return useFormWizardContext<TSteps, TContext, R>(
    useShallow((state) => {
      const value = state.steps[key];
      return selector ? selector(value) : (value as unknown as R);
    }),
  );
}

export function useFormWizardMarkStepDirty<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined = undefined>() {
  return useFormWizardContext<TSteps, TContext, <K extends keyof TSteps>(key: K) => void>(useShallow((state) => state.markStepDirty));
}

export function useFormWizardStepIsDirty<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined = undefined>(
  step: keyof TSteps,
) {
  return useFormWizardContext<TSteps, TContext, boolean>(useShallow((state) => Boolean(state.dirtyStepRecord[step])));
}

export function useFormWizardManager<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined = undefined>() {
  const context = use(FormWizardContext);
  if (!context) {
    throw new Error('useFormWizardManager must be used within a FormWizardContextProvider');
  }

  return context.manager as FormWizardStepBuilderReturnType<TSteps, TContext>;
}
